'use client';
import React, { useState } from 'react';
import { Ship, Zap, Bell, Clock, TrendingDown, TrendingUp, Minus, MapPin, AlertTriangle, CheckCircle2, ChevronDown, RefreshCw,  } from 'lucide-react';

interface VesselTrack {
  id: string;
  vesselName: string;
  imo: string;
  carrier: string;
  voyage: string;
  origin: string;
  destination: string;
  originalEta: string;
  revisedEta: string;
  currentPosition: string;
  status: 'early' | 'on-time' | 'delayed';
  daysDiff: number;
  blNumbers: string[];
  preAlertSent: boolean;
  lastUpdated: string;
}

const MOCK_VESSELS: VesselTrack[] = [
  {
    id: 'v-001',
    vesselName: 'Maersk Emerald',
    imo: 'IMO9876543',
    carrier: 'Maersk',
    voyage: 'AE-1 / 412E',
    origin: 'Nhava Sheva (JNPT)',
    destination: 'Rotterdam',
    originalEta: '20/09/2026',
    revisedEta: '18/09/2026',
    currentPosition: 'Arabian Sea (18.4°N, 63.2°E)',
    status: 'early',
    daysDiff: 2,
    blNumbers: ['MAEU123456789', 'MAEU987001234'],
    preAlertSent: true,
    lastUpdated: '06/09/2026 14:32 IST',
  },
  {
    id: 'v-002',
    vesselName: 'MSC Ambra',
    imo: 'IMO8765432',
    carrier: 'MSC',
    voyage: 'AE-7 / 628W',
    origin: 'Mundra Port',
    destination: 'Felixstowe',
    originalEta: '22/09/2026',
    revisedEta: '22/09/2026',
    currentPosition: 'Suez Canal, Egypt',
    status: 'on-time',
    daysDiff: 0,
    blNumbers: ['MSCU987654321'],
    preAlertSent: false,
    lastUpdated: '05/09/2026 09:18 IST',
  },
  {
    id: 'v-003',
    vesselName: 'Hamburg Express',
    imo: 'IMO7654321',
    carrier: 'Hapag-Lloyd',
    voyage: 'FAL-1 / 301W',
    origin: 'Chennai Port',
    destination: 'Hamburg',
    originalEta: '04/09/2026',
    revisedEta: '07/09/2026',
    currentPosition: 'Mediterranean Sea (36.1°N, 14.8°E)',
    status: 'delayed',
    daysDiff: 3,
    blNumbers: ['HLCU456789012', 'HLCU321654987'],
    preAlertSent: false,
    lastUpdated: '06/09/2026 08:00 IST',
  },
  {
    id: 'v-004',
    vesselName: 'CMA CGM Thalassa',
    imo: 'IMO6543210',
    carrier: 'CMA CGM',
    voyage: 'FAL-3 / 218E',
    origin: 'Kolkata Port',
    destination: 'Le Havre',
    originalEta: '30/09/2026',
    revisedEta: '27/09/2026',
    currentPosition: 'Bay of Bengal (12.3°N, 87.5°E)',
    status: 'early',
    daysDiff: 3,
    blNumbers: ['CMAU789012345'],
    preAlertSent: true,
    lastUpdated: '06/09/2026 12:45 IST',
  },
  {
    id: 'v-005',
    vesselName: 'ZIM Haifa',
    imo: 'IMO5432109',
    carrier: 'ZIM',
    voyage: 'IZX / 114E',
    origin: 'Mundra Port',
    destination: 'Haifa',
    originalEta: '28/09/2026',
    revisedEta: '30/09/2026',
    currentPosition: 'Red Sea (20.5°N, 38.2°E)',
    status: 'delayed',
    daysDiff: 2,
    blNumbers: ['ZIMU556677889'],
    preAlertSent: false,
    lastUpdated: '06/09/2026 10:20 IST',
  },
];

const STATUS_CONFIG = {
  early: {
    label: 'Arriving Early',
    color: 'text-success',
    bg: 'bg-success/10',
    border: 'border-success/30',
    icon: TrendingDown,
    iconColor: 'text-success',
  },
  'on-time': {
    label: 'On Schedule',
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/30',
    icon: Minus,
    iconColor: 'text-primary',
  },
  delayed: {
    label: 'Delayed',
    color: 'text-warning',
    bg: 'bg-warning/10',
    border: 'border-warning/30',
    icon: TrendingUp,
    iconColor: 'text-warning',
  },
};

export default function AutoTrackVessel() {
  const [vessels, setVessels] = useState<VesselTrack[]>(MOCK_VESSELS);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [preAlertSending, setPreAlertSending] = useState<string | null>(null);

  const earlyCount = vessels.filter((v) => v.status === 'early').length;
  const delayedCount = vessels.filter((v) => v.status === 'delayed').length;
  const onTimeCount = vessels.filter((v) => v.status === 'on-time').length;

  const handleRefresh = () => {
    setRefreshing(true);
    // Backend integration point: re-fetch vessel AIS/position data from maritime API
    setTimeout(() => setRefreshing(false), 1500);
  };

  const sendPreAlert = (vesselId: string) => {
    setPreAlertSending(vesselId);
    // Backend integration point: POST /api/pre-alerts with vessel and BL details
    setTimeout(() => {
      setVessels((prev) =>
        prev.map((v) => (v.id === vesselId ? { ...v, preAlertSent: true } : v))
      );
      setPreAlertSending(null);
    }, 1200);
  };

  return (
    <div className="card-surface shadow-card overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-ocean flex items-center justify-center shrink-0">
              <Zap size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground flex items-center gap-2">
                Auto Track — Vessel Intelligence
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-success/10 text-success border border-success/30 px-2 py-0.5 rounded-full">
                  <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                  Live
                </span>
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Automatically detects early arrivals and sends pre-alerts to your customers
              </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 text-xs font-semibold text-muted-foreground border border-border px-3 py-2 rounded-lg hover:bg-muted transition-colors disabled:opacity-50 shrink-0"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Updating...' : 'Refresh Positions'}
          </button>
        </div>

        {/* Summary pills */}
        <div className="flex flex-wrap gap-2 mt-4">
          <div className="flex items-center gap-1.5 bg-success/10 border border-success/30 text-success text-xs font-semibold px-3 py-1.5 rounded-full">
            <TrendingDown size={12} />
            {earlyCount} Arriving Early
          </div>
          <div className="flex items-center gap-1.5 bg-primary/10 border border-primary/30 text-primary text-xs font-semibold px-3 py-1.5 rounded-full">
            <Minus size={12} />
            {onTimeCount} On Schedule
          </div>
          <div className="flex items-center gap-1.5 bg-warning/10 border border-warning/30 text-warning text-xs font-semibold px-3 py-1.5 rounded-full">
            <TrendingUp size={12} />
            {delayedCount} Delayed
          </div>
        </div>

        {/* Early arrival alert banner */}
        {earlyCount > 0 && (
          <div className="mt-3 flex items-center gap-2 bg-success/10 border border-success/30 rounded-lg px-4 py-2.5">
            <Zap size={15} className="text-success shrink-0" />
            <p className="text-xs font-semibold text-success">
              🚀 <span className="font-bold">{earlyCount} vessel{earlyCount > 1 ? 's' : ''}</span> detected arriving ahead of schedule — pre-alerts have been triggered for your customers!
            </p>
          </div>
        )}
      </div>

      {/* Vessel list */}
      <div className="divide-y divide-border">
        {vessels.map((vessel) => {
          const cfg = STATUS_CONFIG[vessel.status];
          const StatusIcon = cfg.icon;
          const isExpanded = expanded === vessel.id;

          return (
            <div key={vessel.id} className="transition-colors hover:bg-muted/30">
              {/* Main row */}
              <div
                className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer"
                onClick={() => setExpanded(isExpanded ? null : vessel.id)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-9 h-9 rounded-xl ${cfg.bg} border ${cfg.border} flex items-center justify-center shrink-0`}>
                    <Ship size={16} className={cfg.iconColor} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-foreground">{vessel.vesselName}</span>
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                        <StatusIcon size={10} />
                        {cfg.label}
                        {vessel.daysDiff > 0 && ` (${vessel.daysDiff}d ${vessel.status === 'early' ? 'early' : 'late'})`}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {vessel.carrier} · {vessel.voyage} · {vessel.blNumbers.length} BL{vessel.blNumbers.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 shrink-0">
                  {/* ETA comparison */}
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-muted-foreground">Original ETA</p>
                    <p className="text-xs font-semibold text-foreground line-through opacity-60">{vessel.originalEta}</p>
                    <p className={`text-xs font-bold ${cfg.color}`}>Revised: {vessel.revisedEta}</p>
                  </div>

                  {/* Pre-alert button */}
                  {vessel.status === 'early' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); sendPreAlert(vessel.id); }}
                      disabled={vessel.preAlertSent || preAlertSending === vessel.id}
                      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all shrink-0 ${
                        vessel.preAlertSent
                          ? 'bg-success/10 text-success border border-success/30 cursor-default' :'bg-accent text-white hover:bg-accent/90 shadow-sm'
                      }`}
                    >
                      {preAlertSending === vessel.id ? (
                        <RefreshCw size={11} className="animate-spin" />
                      ) : vessel.preAlertSent ? (
                        <CheckCircle2 size={11} />
                      ) : (
                        <Bell size={11} />
                      )}
                      {preAlertSending === vessel.id ? 'Sending...' : vessel.preAlertSent ? 'Pre-Alert Sent' : 'Send Pre-Alert'}
                    </button>
                  )}

                  <ChevronDown
                    size={16}
                    className={`text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="px-4 pb-4 fade-in">
                  <div className="bg-muted rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
                    <div>
                      <p className="text-muted-foreground font-medium mb-1">Current Position</p>
                      <div className="flex items-center gap-1.5">
                        <MapPin size={12} className="text-primary shrink-0" />
                        <span className="font-semibold text-foreground">{vessel.currentPosition}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium mb-1">Route</p>
                      <p className="font-semibold text-foreground">{vessel.origin} → {vessel.destination}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium mb-1">IMO Number</p>
                      <p className="font-semibold text-foreground font-tabular">{vessel.imo}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium mb-1">Original ETA</p>
                      <p className="font-semibold text-foreground line-through opacity-60">{vessel.originalEta}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium mb-1">Revised ETA</p>
                      <p className={`font-bold ${cfg.color}`}>{vessel.revisedEta}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground font-medium mb-1">Last Updated</p>
                      <div className="flex items-center gap-1">
                        <Clock size={11} className="text-muted-foreground" />
                        <span className="font-semibold text-foreground">{vessel.lastUpdated}</span>
                      </div>
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <p className="text-muted-foreground font-medium mb-2">BL Numbers on this Vessel</p>
                      <div className="flex flex-wrap gap-2">
                        {vessel.blNumbers.map((bl) => (
                          <span key={`bl-${bl}`} className="bg-background border border-border text-foreground font-tabular font-semibold text-xs px-2.5 py-1 rounded-lg">
                            {bl}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {vessel.status === 'early' && !vessel.preAlertSent && (
                    <div className="mt-3 flex items-start gap-2 bg-success/10 border border-success/30 rounded-lg px-3 py-2.5">
                      <AlertTriangle size={14} className="text-success shrink-0 mt-0.5" />
                      <p className="text-xs text-success font-medium">
                        This vessel is arriving <strong>{vessel.daysDiff} days early</strong>. Click "Send Pre-Alert" to notify your customers immediately so they can arrange customs clearance and delivery in advance.
                      </p>
                    </div>
                  )}
                  {vessel.status === 'delayed' && (
                    <div className="mt-3 flex items-start gap-2 bg-warning/10 border border-warning/30 rounded-lg px-3 py-2.5">
                      <AlertTriangle size={14} className="text-warning shrink-0 mt-0.5" />
                      <p className="text-xs text-warning font-medium">
                        This vessel is delayed by <strong>{vessel.daysDiff} days</strong>. Customers with BLs on this vessel have been notified of the revised ETA.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
